/**
 * `useRecentDestinationsStore` — CCM "smoothest UX" copy/move §1.3.
 *
 * Remembers the last few destinations the lawyer copied/moved a resource to,
 * per session, so the "back and forth" between the same two places is a
 * one-tap chip in both the drag overlay (§1.1) and the type-ahead picker
 * (§1.2). Persisted to sessionStorage (matches the active-context store's
 * lifetime: survives reload, fresh per new tab).
 *
 * A recent entry is the minimal address a `CopyMoveTarget` can be rebuilt
 * from — `kind`, `caseId` (the literal "org" for the org workspace), the
 * display `title`, and the full `address`. `recordDestination` pushes to the
 * FRONT (MRU), de-dupes by `caseId` (so re-using a destination just bumps it
 * to the front), and caps the list at `RECENT_CAP`.
 *
 * The chips a consumer renders must still be re-validated against the CURRENT
 * source scope (a recent dest may be the scope you're now sourcing FROM →
 * an illegal `org→org`/same-case). The pure `recentTargetsForSource` filter
 * handles that, reusing `isValidDropTarget`.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createTenantScopedStorage } from "@/lib/tenant-scoped-storage";
import type { CopyMoveTarget, SourceScope } from "@/lib/copy-move-targets";
import { isValidDropTarget } from "@/lib/copy-move-collision";

/** How many recent destinations to remember (handoff §1.3 "last ~3"). */
export const RECENT_CAP = 3;

/** A remembered destination — exactly a `CopyMoveTarget` (rebuildable into a
 *  picker row / drop chip without a fresh case-list lookup). */
export type RecentDestination = CopyMoveTarget;

interface RecentDestinationsStore {
  recent: RecentDestination[];
  /** Push `target` to the front (MRU), de-dupe by caseId, cap at RECENT_CAP. */
  recordDestination: (target: CopyMoveTarget) => void;
  clear: () => void;
}

/** Pure reducer (exported for unit test) — front-insert, de-dupe by caseId,
 *  cap. The org row de-dupes on the literal "org" caseId. */
export function pushRecent(
  list: readonly RecentDestination[],
  target: CopyMoveTarget,
  cap = RECENT_CAP,
): RecentDestination[] {
  const deduped = list.filter((d) => d.caseId !== target.caseId);
  return [target, ...deduped].slice(0, cap);
}

/**
 * The recent destinations that are VALID targets for `source` (pure;
 * exported for unit test). Drops any recent dest that would be an illegal
 * pairing with the current source (org→org / same-case) — the dest you're
 * sourcing FROM can't also be a destination.
 */
export function recentTargetsForSource(
  recent: readonly RecentDestination[],
  source: SourceScope,
): RecentDestination[] {
  return recent.filter((d) => isValidDropTarget(source, d));
}

export const useRecentDestinationsStore = create<RecentDestinationsStore>()(
  persist(
    (set) => ({
      recent: [],
      recordDestination: (target) =>
        set((state) => ({ recent: pushRecent(state.recent, target) })),
      clear: () => set({ recent: [] }),
    }),
    {
      name: "lexide:recent-copy-move-destinations",
      storage: createJSONStorage(() => createTenantScopedStorage(() => sessionStorage)),
      partialize: (state) => ({ recent: state.recent }),
    },
  ),
);
